import { create } from "zustand";
import moment from "moment";
import {
  salesTarget,
  setSalesTarget,
  slotBooking,
  requestReport,
  getLead,
  getNewLead,
  getCases,
  getReps,
  caseTagWise,
  caseTagDownload,
} from "../api/api";
import secureLocalStorage from "react-secure-storage";
import { LOCAL_STORAGE_CONFIG } from "config";

const { ALTERNATE_EMAIL } = LOCAL_STORAGE_CONFIG;

export const useCaseManagement = create((set) => ({
  salesReps: [],
  targets: [],
  slotData: [],
  postResponse: [],
  slotTagData: [],
  slotTagLoading: false,
  isLoading: false,
  slotLoading: false,
  salesLoading: false,
  error: null,
  fetchSalesRep: async () => {
    try {
      set({ salesLoading: true, error: null });
      const response = await getReps("sales_rep");
      set({ salesReps: response.data, salesLoading: false });
    } catch (error) {
      set({ error: error.message, salesLoading: false });
    }
  },
  fetchTargets: async (sd, ed) => {
    try {
      set({ salesLoading: true, error: null });
      const response = await salesTarget(sd, ed);
      set({ targets: response.data, salesLoading: false });
    } catch (error) {
      set({ error: error.message, salesLoading: false });
    }
  },

  postSalesTargets: async (id, payload, setToaster, refresh, setRefresh) => {
    try {
      set({ salesLoading: true, error: null });
      const promiseData = payload?.map((item) => {
        return setSalesTarget(id, item);
      });
      Promise.all(promiseData)
        .then((results) => {
          setToaster("Sales Target", "Sales target updated", "success");
          setRefresh(!refresh);
        })
        .catch((error) => {
          setToaster("Sales Target", "Failed to update sales target", "danger");
        });
      set({ postResponse: responses.data, salesLoading: false });
    } catch (error) {
      set({ error: error.message, salesLoading: false });
    }
  },
  fetchSlotBooking: async (query) => {
    try {
      set({ slotLoading: true, error: null });
      const response = await slotBooking(query);
      set({ slotData: response.data, slotLoading: false });
    } catch (error) {
      set({ error: error.message, slotLoading: false });
    }
  },
  fetchSlotTagBooking: async (tag, page) => {
    try {
      set({ slotTagLoading: true, error: null });
      const response = await caseTagWise(tag, page);
      set({ slotTagData: response.data, slotTagLoading: false });
    } catch (error) {
      set({ error: error.message, slotTagLoading: false });
    }
  },
  fetchSlotTagDownload: async (tag, toast) => {
    try {
      toast.info(`Downloading please wait for 1-2 mins...`, {
        position: "top-center",
      });
      const response = await caseTagDownload(tag);
      if (response.status !== 200) {
        throw new Error('Failed to download file');
      }
      const csvData = response.data;
      const url = window.URL.createObjectURL(new Blob([csvData],{type:"text/csv"})) 
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', tag)
      document.body.appendChild(link)

      link.click()
      link.remove()

      toast.success(`File Downloaded`, {
        position: "top-center",
      });
    } catch (error) {
      toast.error(`Failed to download`, {
        position: "top-center",
      });
      console.log("Error =", error);
    }
  },

  postRequestReport: async (payload, setToaster) => {
    try {
      set({ isLoading: true, error: null });
      const response = await requestReport(payload);
      setToaster(
        "Report",
        `Your report will be sent to your email ${ALTERNATE_EMAIL} sortly`,
        "success"
      );
    } catch (error) {
      setToaster(
        "Report",
        "Something went wrong, please contact administrator",
        "danger"
      );
      set({ error: error.message, isLoading: false });
    }
  },
}));

export const useLead = create((set) => ({
  leadDataPagination: {},
  leadData: [],
  isLoading: false,
  isError: false,
  error: "",
  fetchLead: async (query) => {
    set({ isLoading: true });
    try {
      const date = moment(new Date()).format("YYYY-MM-DD");
      const response = await getLead(query);
      set({
        leadData: response.data?.data,
        leadDataPagination: response.data?.pagination,
      });
    } catch (error) {
      set({ isError: true });
      set({ leadData: [] });
    } finally {
      set({ isLoading: false });
    }
  },
}));

export const useNewLead = create((set) => ({
  newLeadDataPagination: {},
  newLeadData: [],
  isLoading: false,
  isError: false,
  error: "",
  fetchNewLead: async (query) => {
    set({ isLoading: true });
    try {
      const response = await getNewLead(query.replace("?", ""));
      set({
        newLeadData: response.data?.data,
        newLeadDataPagination: response.data?.pagination,
      });
    } catch (error) {
      set({ isError: true });
      set({ newLeadData: [] });
    } finally {
      set({ isLoading: false });
    }
  },
}));

export const useCases = create((set) => ({
  casesDataPagination: {},
  casesData: [],
  isLoading: false,
  isError: false,
  error: "",
  fetchCases: async (query) => {
    set({ isLoading: true });
    try {
      const response = await getCases(query);
      set({
        casesData: response.data?.data,
        casesDataPagination: response.data?.pagination,
      });
    } catch (error) {
      set({ isError: true });
      set({ casesData: [] });
    } finally {
      set({ isLoading: false });
    }
  },
}));

export const useReps = create((set) => ({
  consultReps: [],
  salesReps: [],
  medReps: [],
  fetchConsultReps: async () => {
    try {
      const response = await getReps("consult_rep");
      set({ consultReps: response.data });
    } catch (error) {
      console.error(error);
    }
  },
  fetchSalesReps: async () => {
    try {
      const response = await getReps("sales_rep");
      set({ salesReps: response.data });
    } catch (error) {
      console.error(error);
    }
  },
  fetchMedReps: async () => {
    try {
      const response = await getReps("medical_rep");
      set({ medReps: response.data });
    } catch (error) {
      console.error(error);
    }
  },
}));
